'use client';

import { FunctionComponent, useState } from 'react';
import { Page } from '../../lib/pageData';
import styles from './DonationPrompts.module.scss';
import { IconArrowRight, IconArrowLeft } from '@justgiving/carepack-icons';
import { DonationPromptTile } from '../DonationPromptTile/DonationPromptTile';
import { useTranslation } from 'react-i18next';
import { DonationPromptDots } from '../DonationPromptDots/DonationPromptDots';
import {
  DonationPromptType,
  ValidDonationPrompt,
} from './helper/donationPromptHelper';
import { getFirstCharity } from '../../utils/relationships';

export type DonationPromptsProps = {
  page: Page;
};

const DonationPrompts: FunctionComponent<DonationPromptsProps> = ({
  page,
}: DonationPromptsProps) => {
  const { t } = useTranslation();
  const [activeTile, setActiveTile] = useState(0);
  const charity = getFirstCharity(page.relationships?.beneficiaries);

  if (!charity || !charity.donationPrompts) {
    return null;
  }

  const donationPrompts: ValidDonationPrompt[] = charity.donationPrompts
    ?.flatMap((prompt) => {
      if (!prompt.description || !prompt.amount || !prompt.type) {
        return [];
      }
      if (prompt.amount <= 0) {
        return [];
      }
      if (
        prompt.type !== DonationPromptType.Monthly &&
        prompt.type !== DonationPromptType.OneOff
      ) {
        return [];
      }
      return {
        description: prompt.description,
        amount: prompt.amount,
        type: prompt.type,
      };
    })
    .sort((a, b) => {
      if (a.amount === b.amount) {
        return a.type === b.type
          ? 0
          : a.type === DonationPromptType.OneOff
            ? -1
            : 1;
      }
      return a.amount - b.amount;
    });

  const currencyCode = 'GBP';

  if (!donationPrompts?.length) {
    return null;
  }

  const setActiveTileWrapper = (newIndex: number) => {
    const maxValue = donationPrompts.length;
    const valueToUse = newIndex < 0 ? maxValue - 1 : newIndex % maxValue;
    setActiveTile(valueToUse);
  };

  return (
    <div className={styles.box}>
      <h1 className={`cp-heading-extra-small ${styles.impactContainer}`}>
        {t('donationPrompt')}
      </h1>
      <div className={styles.container} data-testid="donation-prompts-section">
        <div className={styles.arrows}>
          <button
            aria-label={t('previousPrompt') ?? 'Previous prompt'}
            className={`cp-btn-primary ${styles.arrow}`}
            onClick={() => setActiveTileWrapper(activeTile - 1)}
          >
            <IconArrowLeft className={styles.arrowSvg} />
          </button>
        </div>
        <DonationPromptTile
          key={`donationPrompt-${activeTile}`}
          amount={donationPrompts[activeTile].amount}
          description={donationPrompts[activeTile].description}
          type={donationPrompts[activeTile].type}
          charityId={charity.externalId}
          currency={currencyCode}
        ></DonationPromptTile>
        <div className={styles.arrows}>
          <button
            aria-label={t('nextPrompt') ?? 'Next prompt'}
            className={`cp-btn-primary ${styles.arrow}`}
            onClick={() => setActiveTileWrapper(activeTile + 1)}
          >
            <IconArrowRight className={styles.arrowSvg} />
          </button>
        </div>
      </div>
      <DonationPromptDots
        activeTile={activeTile}
        selectTile={setActiveTile}
        numberOfTiles={donationPrompts.length}
      />
    </div>
  );
};

export default DonationPrompts;
